<!--
  A generic icon renderer component that can display an icon with optional tooltip and messages.
  Props:
    - hide: Boolean to conditionally hide the icon.
    - icon: String representing the icon name (required).
    - color: String for the icon color (default: 'primary').
    - size: String for the icon size (default: 'small').
    - tooltip: String for the tooltip text (default: '').
    - messages: Array of strings to display as messages in the tooltip (default: []).
-->
<template>
  <div v-if="!params.hide" class="icon-renderer">
    <v-tooltip v-bind="tooltipProps" location="bottom">
      <template #activator="{ props: activatorProps }">
        <v-icon v-bind="{ ...params, ...activatorProps }" />
      </template>
      <p v-for="(message, i) in messages" :key="i" class="icon-renderer__message">
        {{ message }}
      </p>
    </v-tooltip>
  </div>
</template>

<script setup>
import { isArray } from 'lodash';
import { computed, defineProps } from 'vue';

// Since we allow AG Grid to use this component, we have to parse props from props.params.
// This is annoying because we can't define default props in the usual way, but it's necessary for AG Grid compatibility.
const localProps = defineProps({
  params: {
    type: Object,
    default: () => ({
      /* eslint-disable no-undef */
      hide: props.params.hide,
      icon: props.params.icon,
      color: props.params.color,
      size: props.params.size,
      tooltip: props.params.tooltip,
      messages: props.params.messages,
      /* eslint-enable no-undef */
    }),
  },
});

const tooltipProps = computed(() => ({
  location: 'top',
  'content-class': 'rtls-tooltip',
  ...localProps.params,
}));

const messages = computed(() => {
  const msgs = localProps.params.messages;
  if (!msgs) return [];
  return isArray(msgs) ? msgs : [msgs];
});
</script>

<style lang="scss">
// Note that it is important this <style> tag isn't "scoped" otherwise the alert-icon
// styling doesn't take affect since the tooltip is added by Vuetify to one of the root elements in the DOM.
@import '@style/base/_variables.scss';

.icon-renderer {
  &__message {
    font-size: 1.25rem;
  }
}
</style>
